import { useMutation, useQuery } from "@apollo/client";
import type { IConfirmDialogProps } from "@fluidattacks/design";
import { Alert } from "@fluidattacks/design";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IPolicy, IPolicyCriteria } from "./types";

import {
  GET_ORGANIZATION_PRIORITY_POLICIES,
  REMOVE_ORGANIZATION_PRIORITY_POLICY,
  UPDATE_ORGANIZATION_PRIORITY_POLICIES,
} from "../queries";
import { useAudit } from "hooks";
import { Logger } from "utils/logger";
import { msgError, msgSuccess } from "utils/notifications";

interface IUsePriorityPoliciesActions {
  criteria: IPolicy[];
  handleDeletion: (priorityPolicy: IPolicy) => () => Promise<void>;
  handleSubmit: (values: IPolicyCriteria) => Promise<void>;
  loading: boolean;
}

const prefix = "organization.tabs.policies.priority.";
const modifyError =
  "Exception - The user already modify the policies for the same organization";

const usePriorityPoliciesActions = (
  close: () => void,
  confirm: (props: Readonly<IConfirmDialogProps>) => Promise<boolean>,
  organizationId: string,
): IUsePriorityPoliciesActions => {
  const { t } = useTranslation();
  const { addAuditEvent } = useAudit();

  const { data, loading } = useQuery(GET_ORGANIZATION_PRIORITY_POLICIES, {
    fetchPolicy: "cache-and-network",
    onCompleted: (): void => {
      addAuditEvent("OrgPriorityPolicy", organizationId);
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.warning(
          "An error occurred fetching organization priority policies",
          error,
        );
      });
    },
    variables: { organizationId },
  });
  const [removePriorityPolicy, { loading: removeLoading }] = useMutation(
    REMOVE_ORGANIZATION_PRIORITY_POLICY,
    {
      context: { skipGlobalErrorHandler: true },
      onCompleted: (): void => {
        msgSuccess(t(`${prefix}removeSuccess`));
      },
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error): void => {
          if (error.message === modifyError) {
            msgError(t(`${prefix}error`));
          } else {
            msgError(t("groupAlerts.errorTextsad"));
            Logger.warning(
              "An error occurred removing organization priority policy",
              error,
            );
          }
        });
      },
      refetchQueries: [GET_ORGANIZATION_PRIORITY_POLICIES],
    },
  );
  const [updatePriorityPolicies, { loading: updateLoading }] = useMutation(
    UPDATE_ORGANIZATION_PRIORITY_POLICIES,
    {
      context: { skipGlobalErrorHandler: true },
      onCompleted: (): void => {
        msgSuccess(t(`${prefix}success`));
      },
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error): void => {
          if (error.message === modifyError) {
            msgError(t(`${prefix}error`));
          } else {
            msgError(t("groupAlerts.errorTextsad"));
            Logger.warning(
              "An error occurred updating organization priority policies",
              error,
            );
          }
        });
      },
      refetchQueries: [GET_ORGANIZATION_PRIORITY_POLICIES],
    },
  );

  const criteria = useMemo((): IPolicy[] => {
    return (data?.organization.priorityPolicies ?? []).map(
      ({ policy, value }): IPolicy => ({ policy, value }),
    );
  }, [data]);

  const handleSubmit = useCallback(
    async (values: IPolicyCriteria): Promise<void> => {
      const uniquePolicies = criteria.filter(
        ({ policy: storedPolicy }): boolean =>
          !values.criteria
            .map(({ policy }): string => policy)
            .includes(storedPolicy),
      );
      const policiesToUpdate = uniquePolicies.concat(
        values.criteria
          .filter(({ policy }): boolean => policy !== "")
          .map(({ policy, value }): { policy: string; value: number } => ({
            policy,
            value,
          })),
      );

      const confirmResult = await confirm({
        id: "confirm-update",
        message: (
          <Alert variant={"info"}>
            {t(`${prefix}confirmUpdateModal.description`)}
          </Alert>
        ),
        title: t(`${prefix}confirmUpdateModal.title`),
      });

      if (confirmResult) {
        close();

        await Promise.all([
          updatePriorityPolicies({
            variables: { organizationId, priorityPolicies: policiesToUpdate },
          }),
        ]);
      }
    },
    [close, confirm, criteria, updatePriorityPolicies, organizationId, t],
  );

  const handleDeletion = useCallback(
    (priorityPolicy: IPolicy): (() => Promise<void>) => {
      return async (): Promise<void> => {
        const confirmResult = await confirm({
          message: t(`${prefix}confirmModal.description`, {
            criteria: priorityPolicy.policy.split("(")[0],
          }),
          title: t(`${prefix}confirmModal.title`),
        });
        if (confirmResult) {
          await Promise.all([
            removePriorityPolicy({
              variables: {
                organizationId,
                priorityPolicy: priorityPolicy.policy,
              },
            }),
          ]);
        }
      };
    },
    [confirm, organizationId, removePriorityPolicy, t],
  );

  return {
    criteria,
    handleDeletion,
    handleSubmit,
    loading: loading || updateLoading || removeLoading,
  };
};

export { usePriorityPoliciesActions };
